import { useState, useRef, useEffect, useCallback } from "react";
import { categories, mainSiteUrl } from "@/mocks/categories";
import { Link } from "react-router-dom";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isHero = !scrolled && !mobileMenuOpen;

  return (
    <nav
      id="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isHero
          ? "bg-gradient-to-b from-black/50 to-transparent"
          : "bg-background-50/95 backdrop-blur-md border-b border-background-200/60"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[4.5rem]">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2 group">
            <span className={`font-heading text-lg md:text-xl font-bold tracking-tight transition-colors ${isHero ? "text-white" : "text-foreground-900"}`}>
              Hoobuy<span className="text-accent-500">.Tattoo</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* Home */}
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                isHero
                  ? "text-white/90 hover:text-white hover:bg-white/10"
                  : "text-foreground-700 hover:text-foreground-900 hover:bg-background-100"
              }`}
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                <i className="ri-home-4-line"></i>
              </span>
              Início
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/hoobuyspreadsheet"
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                isHero
                  ? "text-white/90 hover:text-white hover:bg-white/10"
                  : "text-foreground-700 hover:text-foreground-900 hover:bg-background-100"
              }`}
            >
              <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                <i className="ri-book-open-line"></i>
              </span>
              Guia de Compras
            </Link>

            {/* Categories Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
                  isHero
                    ? "text-white/90 hover:text-white hover:bg-white/10"
                    : "text-foreground-700 hover:text-foreground-900 hover:bg-background-100"
                }`}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-grid-line"></i>
                </span>
                Categorias
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white rounded-xl overflow-hidden border border-background-200/70 shadow-xl z-50">
                  <div className="max-h-[420px] overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:text-foreground-900 hover:bg-background-50 transition-colors cursor-pointer whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-8 h-8 rounded-lg bg-accent-50 flex items-center justify-center text-accent-500 flex-shrink-0">
                          <i className="ri-arrow-right-up-line text-sm"></i>
                        </span>
                        {cat.namePt}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Hoobuy Sheet */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`ml-2 px-4 py-2 text-sm font-medium rounded-full border transition-all cursor-pointer whitespace-nowrap ${
                isHero
                  ? "text-white border-white/50 hover:bg-white hover:text-foreground-900"
                  : "text-foreground-700 border-foreground-300/60 hover:border-foreground-500"
              }`}
            >
              Hoobuy Sheet
            </a>

            {/* Buy It Now */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden flex items-center justify-center w-10 h-10 rounded-lg transition-colors cursor-pointer ${
              isHero ? "text-white hover:bg-white/10" : "text-foreground-700 hover:bg-background-100"
            }`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
            </span>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 flex flex-col gap-1">
            {/* Home */}
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-50 rounded-xl transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-700">
                <i className="ri-home-4-line text-lg"></i>
              </span>
              Início
            </Link>

            {/* Shopping Guide */}
            <Link
              to="/hoobuyspreadsheet"
              className="flex items-center gap-3 px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-50 rounded-xl transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center text-accent-500">
                <i className="ri-book-open-line text-lg"></i>
              </span>
              Guia de Compras
            </Link>

            {/* Categories Toggle */}
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-50 rounded-xl transition-colors cursor-pointer"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center text-foreground-700">
                  <i className="ri-grid-line text-lg"></i>
                </span>
                Categorias
              </span>
              <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${mobileCatOpen ? "rotate-180" : ""}`}>
                <i className="ri-arrow-down-s-line"></i>
              </span>
            </button>

            {mobileCatOpen && (
              <div className="ml-3 pl-3 border-l-2 border-background-100 flex flex-col gap-0.5">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-600 hover:text-foreground-900 hover:bg-background-50 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-6 h-6 rounded bg-accent-50 flex items-center justify-center text-accent-500 flex-shrink-0">
                      <i className="ri-arrow-right-up-line text-xs"></i>
                    </span>
                    {cat.namePt}
                  </a>
                ))}
              </div>
            )}

            <div className="border-t border-background-100 my-2"></div>

            {/* CTA Buttons */}
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium text-foreground-800 border border-foreground-300/60 rounded-full hover:bg-background-50 transition-colors cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line"></i>
              </span>
              Hoobuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-primary-500 rounded-full transition-all cursor-pointer"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shopping-bag-line"></i>
              </span>
              Buy It Now
            </a>
          </div>
        </div>
      )}
    </nav>
  );
}